import React from "react"

import {
  Button,
  ChoiceCard,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"

import { AudioPreviewButton } from "./AudioPreviewButton"

interface OptionCardProps {
  label: string
  description?: string
  badge?: React.ReactNode
  previewUrl?: string
  previewVolume?: number
  isSelected: boolean
  isDisabled?: boolean
  currentlyPlayingAudioId: string | null
  onPlayingChange: (playingId: string | null) => void
  onSelect: () => void
  /** "plain" for the current choice on the form; the picker keeps the card. */
  variant?: "card" | "plain"
}

/**
 * A `ChoiceCard` with an audio preview in its trailing slot, so every audio
 * picker on the persona page reads the same way.
 */
export function OptionCard({
  label,
  description,
  badge,
  previewUrl,
  previewVolume,
  isSelected,
  isDisabled = false,
  currentlyPlayingAudioId,
  onPlayingChange,
  onSelect,
  variant,
}: OptionCardProps) {
  return (
    <ChoiceCard
      title={label}
      description={description}
      badge={badge}
      selected={isSelected}
      disabled={isDisabled}
      onSelect={onSelect}
      variant={variant}
      className="gap-2 p-2"
      trailing={
        previewUrl ? (
          <AudioPreviewButton
            previewUrl={previewUrl}
            audioLabel={label}
            volume={previewVolume}
            currentlyPlayingId={currentlyPlayingAudioId}
            onPlayingChange={onPlayingChange}
          />
        ) : undefined
      }
    />
  )
}

interface PersonaOptionPickerDialogProps {
  isOpen: boolean
  title: string
  description?: React.ReactNode
  onClose: () => void
  /** Rendered above the option cards, e.g. a warning. */
  banner?: React.ReactNode
  /** Rendered below the option cards, e.g. a volume slider or actions. */
  footer?: React.ReactNode
  children: React.ReactNode
}

/**
 * Shell for a call-environment picker: title, optional note, option cards,
 * and Done. Selection applies as you click, the same as the face customizer.
 */
export function PersonaOptionPickerDialog({
  isOpen,
  title,
  description,
  onClose,
  banner,
  footer,
  children,
}: PersonaOptionPickerDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <p className="text-xs text-coval-text-muted">{description}</p>
          )}
        </DialogHeader>
        <DialogBody className="space-y-3">
          {banner}
          <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
            {children}
          </div>
          {footer}
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
